import { useState, useEffect, Dispatch } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { CircleExclamation } from "@unified-checkout/design-system/icons/index";
import { PageStatus } from "@unified-checkout/design-system";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import type { TCountry, IField } from "@app/dashboard/types/outerLayout/common";
import type {
  IUserActivationFields,
  IUserActivationSteps,
  TSubmissionResponseData
} from "@app/dashboard/types/outerLayout/user-activation";
import {
  getInvitationLinkStatus
} from "@app/dashboard/services/outer_layout/user-activation";
import {
  passwordsComparisonLiteral,
  validateField as fieldValidator,
  required,
  passwordValidations,
  passwordMatchValidation,
  emailValidation,
  optionalButHasValidation,
  phoneNumberValidation
} from '@app/dashboard/utils/formValidations';
import { getCountryFromDomain } from "@app/dashboard/utils/getCountryFromDomain";
import { tokenDecoder } from '@app/dashboard/utils/tokenDecoder';
import { BlueCheckMarkCircular } from "@app/dashboard/assets/icons";
import { OUTER_ROUTES } from "@app/dashboard/routes/outer-routes";
import { FullPageLoader } from "@app/dashboard/components/shared/FullPageLoader";
import FormIntro from '@app/dashboard/components/outer_layout/FormIntro';
import FinalStepInfo from "@app/dashboard/components/outer_layout/FinalStepInfo";
import UserActivationForm from "./steps/UserActivationForm";
import OtpStep from "./steps/OtpStep";
import { COUNTRIES_INFO } from "@app/dashboard/constants/countries";


const ONE_SECOND_TIMEOUT = 1000;
export const LOGIN_REDIRECT_TIMEOUT = 3 * ONE_SECOND_TIMEOUT;

const country: TCountry = getCountryFromDomain();

const STEPS: IUserActivationSteps = {
  USER_ACTIVATION: 1,
  OTP: 2,
  SUCCESSFUL_ACTIVATION: 3,
  ACCOUNT_ALREADY_ACTIVATED: 4,
};

const FieldNames: IUserActivationFields = {
  FULL_NAME: "full_name",
  EMAIL: "email",
  PHONE_NUMBER: "phone_number",
  PASSWORD: "password",
  CONFIRM_PASSWORD: "confirm_password",
};

const UserActivation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const decodedToken = token && tokenDecoder(token) as any;

  const {
    isPending: invitationLinkStatusLoading,
    error,
    status: invitationLinkStatusStatus,
    mutate: mutateInvitationLinkStatus
  } = useMutation({
    mutationKey: [QUERY_KEYS.INVITATION_STATUS],
    mutationFn: () => getInvitationLinkStatus(token || '').then(res => res.data.data),
  });

  useEffect(() => {
    token && mutateInvitationLinkStatus();
  }, [token, mutateInvitationLinkStatus]);

  const invitationLinkStatusError = (error as AxiosError)?.response?.data as any;

  const {
    user_name_field,
    email,
    phone_number
  } = decodedToken || {};

  const phoneNumberWithoutCountryCode = phone_number?.replace(COUNTRIES_INFO[country].phone_country_code, '') || '';

  const isEmail = user_name_field === 'email';
  const userIdentifier = isEmail ? email : phoneNumberWithoutCountryCode;

  const isInvitedBy = isEmail ? 'email' : 'phone';

  const fieldsInitialState = {
    [FieldNames.FULL_NAME]: {
      value: '',
      isValid: undefined,
      errorMsg: '',
      validations: [required]
    },
    [FieldNames.EMAIL]: {
      value: isInvitedBy === 'email' ? userIdentifier : '',
      isValid: true,
      errorMsg: '',
      validations: [optionalButHasValidation(emailValidation)]
    },
    [FieldNames.PHONE_NUMBER]: {
      value: isInvitedBy === 'phone' ? userIdentifier : '',
      isValid: true,
      errorMsg: '',
      validations: [optionalButHasValidation(phoneNumberValidation(country))]
    },
    [FieldNames.PASSWORD]: {
      value: undefined,
      isValid: undefined,
      errorMsg: '',
      validations: passwordValidations
    },
    [FieldNames.CONFIRM_PASSWORD]: {
      value: undefined,
      isValid: undefined,
      errorMsg: '',
      validations: [required, passwordMatchValidation]
    },
  };

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.USER_ACTIVATION);
  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [submissionResponseData, setSubmissionResponseData] = useState<TSubmissionResponseData>();


  const validateField = (fields: IField, setFields: Dispatch<(prevState: IField) => IField>, fieldName: string, value: string) => {

    let valueToValidate = value;

    if (fieldName === FieldNames.CONFIRM_PASSWORD) {
      const passwordValue = fields[FieldNames.PASSWORD].value as string;
      const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value as string;

      const passwordsLiteral = passwordsComparisonLiteral(passwordValue, confirmPasswordValue);
      valueToValidate = passwordsLiteral;
    };

    fieldValidator(fields, setFields, fieldName, valueToValidate);
  };

  const redirectToLogin = () => {
    navigate(OUTER_ROUTES.LOGIN, {
      state: { activatedUsername: isInvitedBy === 'email' ? fields[FieldNames.EMAIL].value : fields[FieldNames.PHONE_NUMBER].value },
    })
  };

  const formTitles = {
    [STEPS.USER_ACTIVATION]: t('outerLayout.form.signUp'),
    [STEPS.OTP]: isInvitedBy === 'phone' ? t('outerLayout.form.userActivation.verifyEmail') : t('outerLayout.form.userActivation.verifyMobileNumber'),
    [STEPS.SUCCESSFUL_ACTIVATION]: "",
    [STEPS.ACCOUNT_ALREADY_ACTIVATED]: "",
  };

  const formSubTitles = {
    [STEPS.USER_ACTIVATION]: '',
    [STEPS.OTP]:
      t('outerLayout.form.userActivation.enterOtp',
        isInvitedBy === 'phone' ?
          { email_or_phone_value: submissionResponseData?.email, email_or_phone_text: t('form.email') }
          :
          { email_or_phone_value: submissionResponseData?.phone || '', email_or_phone_text: t('form.phoneNumber') }

      ),
    [STEPS.SUCCESSFUL_ACTIVATION]: "",
    [STEPS.ACCOUNT_ALREADY_ACTIVATED]: "",
  };


  return (
    (invitationLinkStatusStatus === 'idle' || invitationLinkStatusLoading) ?
      <FullPageLoader />
      :
      invitationLinkStatusError ?
        <PageStatus
          status="error"
          icon={CircleExclamation}
          title={invitationLinkStatusError?.message || t('errors.somethingWentWrong')}
          description={invitationLinkStatusError?.details}
        />
        :
        <FormIntro
          formLegend={formTitles[currentStep]}
          subtitle={formSubTitles[currentStep]}
        >
          {
            currentStep === STEPS.USER_ACTIVATION &&
            <UserActivationForm
              validateField={validateField}
              loading={loading}
              setLoading={setLoading}
              redirectToLogin={redirectToLogin}
              setCurrentStep={setCurrentStep}
              STEPS={STEPS}
              country={country}
              setSubmissionResponseData={setSubmissionResponseData}
              fields={fields}
              setFields={setFields}
              FieldNames={FieldNames}
              LOGIN_REDIRECT_TIMEOUT={LOGIN_REDIRECT_TIMEOUT}
              isInvitedBy={isInvitedBy}
              token={token as string}
            />
          }
          {
            currentStep === STEPS.OTP &&
            <OtpStep
              loading={loading}
              setLoading={setLoading}
              setCurrentStep={setCurrentStep}
              otpToken={submissionResponseData?.otpToken as string}
              STEPS={STEPS}
              fields={fields}
              FieldNames={FieldNames}
              isInvitedBy={isInvitedBy}
              redirectToLogin={redirectToLogin}
              LOGIN_REDIRECT_TIMEOUT={LOGIN_REDIRECT_TIMEOUT}
            />
          }
          {
            currentStep === STEPS.SUCCESSFUL_ACTIVATION &&
            <FinalStepInfo
              text={t('outerLayout.form.register.thanksForChoosingPaymob')}
              img={BlueCheckMarkCircular}
            />
          }
        </FormIntro>
  )
}

export default UserActivation;